import { useEffect, useState } from "react";
import axios from "axios";
import { SITE } from "../lib/site";
import { Quote, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static testimonials that stay regardless of DB state (e.g. Cristobal's
// real Facebook review). The "featured" one renders full-width on top.
const STATIC_TESTIMONIALS = SITE.testimonials.filter((t) => t.featured);

function formatDetail(r) {
  const parts = [];
  if (r.location) parts.push(r.location);
  if (r.vehicle) parts.push(r.vehicle);
  return parts.join(" · ");
}

export default function Testimonials() {
  const [dbReviews, setDbReviews] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews`, {
          params: { approved_only: true },
        });
        if (!cancelled && Array.isArray(data)) setDbReviews(data);
      } catch {
        // Silently fall back to static testimonials
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Merge: featured static testimonials first (Cristobal), then approved DB reviews
  const combined = [
    ...STATIC_TESTIMONIALS,
    ...dbReviews.map((r) => ({
      quote: r.quote,
      name: r.name,
      detail: formatDetail(r),
      featured: false,
    })),
  ];

  return (
    <section
      id="reviews"
      data-testid="testimonials-section"
      className="relative py-28 lg:py-36 bg-[#050505] overflow-hidden"
    >
      <Quote
        className="absolute top-16 right-12 text-gold/5 hidden lg:block"
        size={320}
        strokeWidth={1}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-gold" />
            <span className="text-xs uppercase tracking-[0.32em] text-gold/90">
              Reviews
            </span>
          </div>
          <h2 className="font-display font-light text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            What our clients
            <span className="italic gold-shimmer"> say</span>.
          </h2>
          <p className="mt-6 text-white/55 max-w-md text-sm">
            Real reviews from real Shine &amp; Drive customers. More coming
            as we keep detailing cars across Northeast Seattle.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {combined.map((t, i) => (
            <figure
              key={i}
              data-testid={`testimonial-${i}`}
              className={`relative bg-[#0a0a0a] border p-8 lg:p-10 rounded-sm glow-card ${
                t.featured
                  ? "md:col-span-2 border-gold/40"
                  : "border-white/10"
              }`}
            >
              {t.featured && (
                <span
                  data-testid={`testimonial-featured-${i}`}
                  className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-gold text-black px-3 py-1 text-[10px] uppercase tracking-[0.22em] rounded-full font-medium"
                >
                  Featured Review
                </span>
              )}
              <div className="flex items-center gap-1 text-gold">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <blockquote className="mt-6 font-display italic text-2xl lg:text-3xl leading-snug text-white/90">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="text-white text-sm font-medium tracking-wide">
                  {t.name}
                </span>
                {t.detail && (
                  <span className="text-[11px] uppercase tracking-[0.22em] text-gold/80">
                    {t.detail}
                  </span>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
